import React, { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import CustomInput from "../TextField/TextFeild";
import TimezoneSelect from "../TimeZoneDropDown/TimezoneSelect";
import moment from "moment";
import { removeSelectedContest, startContest, updateContest } from "../../store/slices/contestSlice";
import { useSocket } from "./../../context/SocketContext";
import { toast } from "react-toastify";
import {
    FaPlus,
    FaImage,
    FaTimes
} from "react-icons/fa";

// Import CKEditor
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import CropperModalAdmin from "./CropperModalAdmin";
import { HUMAN_TIMEZONES } from "../../constants";

const UpdateContest = ({ show, onClose }) => {
    const dispatch = useDispatch();
    const auth = useSelector((state) => state.auth);
    const { socket } = useSocket();

    const roomId = auth?.user?.attributes?.stationChatId;

    const { selectedContest } = useSelector((state) => state.contest);
    const [step, setStep] = useState(1);
    const [followersOnly, setFollowersOnly] = useState(false);
    const [showQuestions, setShowQuestions] = useState(false);

    const [showModal, setShowModal] = useState(false);
    const [image, setImage] = useState(null);
    const [croppedImage, setCroppedImage] = useState(null);
    const [description, setDescription] = useState("");
    const [deletedAnswers, setDeletedAnswers] = useState([]);
    const [isImageDeleted, setIsImageDeleted] = useState(false);

    const validationSchema = Yup.object().shape({
        prize: Yup.string().required("Prize is required"),
        nWinners: Yup.number()
            .required("Number of winners is required")
            .positive("Must be positive")
            .integer("Must be integer"),
        rules: Yup.string().max(200, "Title cannot be longer than 200 characters"),
        ...(showQuestions && {
            title: Yup.string()
                .max(200, "Must be 200 characters or less")
                .required("Question is required"),
            answers: Yup.array()
                .of(
                    Yup.object().shape({
                        title: Yup.string()
                            .trim()
                            .required("Answer is required"),
                    })
                )
                .min(2, "At least two answers are required"),
        }),
        tz: Yup.string(),

        startDate: Yup.string().when("tz", {
            is: (tz) => tz && tz.length > 0,
            then: (schema) => schema.required("Start date is required when timezone is selected"),
        }),

        startTime: Yup.string().when("tz", {
            is: (tz) => tz && tz.length > 0,
            then: (schema) =>
                schema
                    .required("Start time is required when timezone is selected")
                    .test(
                        "is-future-5-min",
                        "Start time must be at least 5 minutes in the future",
                        function (value) {
                            let { startDate, tz } = this.parent;
                            tz = HUMAN_TIMEZONES[tz];
                            if (selectedContest.attributes.state == 1 || selectedContest.attributes.state == 2) return true; // contest is already started so dont validate the start date.
                            if (selectedContest.attributes.isScheduled != 0) return true;
                            if (!startDate || !value || !tz) return true;

                            // Convert to moment object
                            const start = moment.tz(
                                `${startDate} ${value}`,
                                "YYYY-MM-DD HH:mm",
                                tz
                            );
                            const now = moment.tz(tz);
                            const minutesUntil = start.diff(now, 'minutes', true);

                            console.log("timezone:", tz);
                            console.log("startInTZ:", start.format());
                            console.log("now:", now.format());
                            console.log("minutesUntil:", minutesUntil);

                            // Validation
                            if (minutesUntil < 5) {
                                return this.createError({
                                    message: `Start Time must be 5 minutes in advance.`
                                });
                            }

                            return true;
                        }
                    ),
        }),

        endDate: Yup.string().when("tz", {
            is: (tz) => tz && tz.length > 0,
            then: (schema) => schema.required("End date is required when timezone is selected"),
        }),

        endTime: Yup.string().when(["tz", "startDate", "startTime"], {
            is: (tz, sd, st) => tz && sd && st,
            then: (schema) =>
                schema
                    .required("End time is required when timezone is selected")
                    .test(
                        "is-after-start",
                        "End time must be after the start time",
                        function (value) {
                            let { startDate, startTime, endDate, tz } = this.parent;
                            tz = HUMAN_TIMEZONES[tz];
                            if (!startDate || !startTime || !endDate || !value || !tz)
                                return true;

                            const start = moment.tz(
                                `${startDate} ${startTime}`,
                                "YYYY-MM-DD HH:mm",
                                tz
                            );

                            const end = moment.tz(
                                `${endDate} ${value}`,
                                "YYYY-MM-DD HH:mm",
                                tz
                            );

                            return end.isAfter(start);
                        }
                    ),
        }),
    });

    const handleCrop = (croppedImg) => {
        console.log(croppedImg, 'croppedImg')
        setCroppedImage(croppedImg);
        formik.setFieldValue("image", croppedImg);
        setShowModal(false);
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = () => {
                setImage(reader.result);
                setShowModal(true);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleDeleteImage = () => {
        setIsImageDeleted(true);
    };

    const handleUndoDeleteImage = () => {
        setIsImageDeleted(false);
    };

    const removeImage = () => {
        setCroppedImage(null);
        setImage(null);
        formik.setFieldValue("image", "");
        setIsImageDeleted(false);
    };

    // Reset function to clear all state when modal closes
    const resetAllState = () => {
        setStep(1);
        setFollowersOnly(false);
        setShowQuestions(false);
        setImage(null);
        setCroppedImage(null);
        setDescription("");
        setShowModal(false);
        setDeletedAnswers([]);
        setIsImageDeleted(false);

        // Reset formik form
        formik.resetForm();
    };

    const handleClose = () => {
        resetAllState();
        onClose();
    };

    const formik = useFormik({
        initialValues: {
            title: "",
            answers: ["", ""],
            tz: "",
            startDate: "",
            startTime: "",
            endDate: "",
            endTime: "",
            prize: "",
            nWinners: "",
            rules: "",
            image: "",
            description: "",
        },
        validationSchema,
        enableReinitialize: true,
        validateOnChange: false,
        onSubmit: async (values, { resetForm }) => {
            if (step === 1 && selectedContest?.attributes?.isScheduled != 0) {
                const errors = await formik.validateForm();
                if (Object.keys(errors).length === 0) {
                    setStep(2);
                }
            } else {
                // Filter out deleted answers
                const activeAnswers = values.answers
                    .map((ans, idx) => ({ ...ans, _index: idx }))
                    .filter((ans, idx) => !deletedAnswers.includes(idx))
                    .map(({ _index, ...ans }) => ans);
                
                const contestData = {
                    ...values,
                    answers: activeAnswers,
                    image: isImageDeleted ? "" : values.image,
                    followersOnly,
                    hasQuestions: showQuestions,
                    contestId: selectedContest.id
                };
                try {
                    const data = await dispatch(updateContest({ data: contestData })).unwrap();

                    const pollId = data.id;
                    socket.emit(`poll.contest.updated`, { contest: data, roomId });

                    resetAllState();
                    onClose();

                } catch (error) {
                    toast.error(error.message || 'Failed to update contest. Please try again.');
                }
            }
        },
    });

    useEffect(() => {
        if (show && selectedContest) {
            // Reset delete states
            setDeletedAnswers([]);
            setIsImageDeleted(false);
            
            // Only set values when modal is shown and selectedContest is available
            const contest = selectedContest;
            const poll = contest.relations?.poll?.data;

            let displayData = {};
            try {
                displayData = contest?.attributes?.displayData ? JSON.parse(contest?.attributes?.displayData) : {};
            } catch (e) {
                console.error("Error parsing displayData", e);
            }

            // Set existing image if available
            const existingImage = contest.attributes?.image?.trim();
            if (existingImage) {
                setCroppedImage(existingImage);
            }

            // Set existing description if available
            const existingDescription = contest.attributes?.description || "";
            setDescription(existingDescription);

            // Set form values
            formik.setValues({
                title: poll?.attributes?.title || "",
                answers: poll?.relations?.answers?.data?.map((ans) => ({
                    id: ans.id,
                    title: ans.attributes.title
                })) || [{ id: "", title: "" }, { id: "", title: "" }],
                tz: contest.attributes?.tz || "",
                startDate: displayData?.startDate || "",
                startTime: displayData?.startTime || "",
                endDate: displayData?.endDate || "",
                endTime: displayData?.endTime || "",
                prize: contest.attributes?.prize || "",
                nWinners: contest.attributes?.nWinners || "",
                rules: contest.attributes?.meta?.rules || "",
                image: existingImage || "",
                description: existingDescription,
            });

            setShowQuestions(contest.attributes?.hasQuestions);
            setFollowersOnly(contest.attributes?.onlyFollowers);
            if (contest.attributes.isScheduled && contest.attributes.isScheduled != 0) {
                setStep(2);
            } else {
                setStep(1);
            }
            console.log(selectedContest,'selectedContest')
        }
    }, [show, selectedContest]); // Added show to dependency array

    const handleAnswerChange = (index, value) => {
        const newAnswers = [...formik.values.answers];
        newAnswers[index] = {
            ...newAnswers[index],
            title: value
        };
        formik.setFieldValue("answers", newAnswers);
    };

    const addAnswer = () => {
        if (formik.values.answers.length < 4) {
            formik.setFieldValue("answers", [...formik.values.answers, ""]);
        }
    };

    const setFormikDescription = (data) => {
        setDescription(data);
        formik.setFieldValue("description", data);
    };

    const handleDeleteAnswer = (index) => {
        setDeletedAnswers([...deletedAnswers, index]);
    };

    const handleUndoDeleteAnswer = (index) => {
        setDeletedAnswers(deletedAnswers.filter((i) => i !== index));
    };

    const removeAnswer = (index) => {
        const newAnswers = formik.values.answers.filter((_, i) => i !== index);
        formik.setFieldValue("answers", newAnswers);
        // Remove from deletedAnswers if it was there
        setDeletedAnswers(deletedAnswers.filter((i) => i !== index).map(i => i > index ? i - 1 : i));
    };

    // Check if we have existing image or description
    const hasExistingImage = selectedContest?.attributes?.image?.trim();
    const hasExistingDescription = selectedContest?.attributes?.description?.trim();

    return (
        <>
            <Modal
                show={show}
                size="xl"
                onHide={handleClose}
                centered
                backdrop="static"
                keyboard={false}
            >
                <form onSubmit={formik.handleSubmit} className="row m-3">
                    <div className="col-md-12 " style={{ borderBottom: "1px solid grey" }}>
                        {step === 1 && (
                            <div className="row mb-3">
                                <div className="col-md-6 d-flex align-items-center">
                                    <p className="h4 mb-0">Update Contest</p>
                                </div>
                                <div className="col-md-12">
                                    <p className="text-muted mb-3">
                                        You can update a contest and start it. You will be able to alter it and manually start and stop it.
                                    </p>
                                </div>
                            </div>
                        )}
                        {step === 2 && (
                            <>
                                <h4 className="mb-2">Start Contest Now or Schedule for Later</h4>
                                <p className="text-muted mb-3">
                                    You can schedule Contest to begin at your preferred time and date in the future.
                                </p>
                            </>
                        )}
                    </div>
                    <div className="col-md-6 shadow-none p-3 m-0" style={{ borderRight: "1px solid grey" }}>
                        {step === 1 && (
                            <>
                                <div className="px-2">
                                    <p className="text-muted mb-3">
                                        You can update the contest details and schedule.
                                    </p>
                                    <div className="mb-3 d-flex justify-content-start align-items-center w-50 gap-3">
                                        <label className="form-label fw-bold mb-0">Followers Only</label>
                                        <div className="form-check form-switch">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                checked={followersOnly}
                                                onChange={() => setFollowersOnly(!followersOnly)}
                                            />
                                        </div>
                                    </div>
                                    <CustomInput
                                        label="prize"
                                        id="prize"
                                        name="prize"
                                        value={formik.values.prize}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        error={formik.touched.prize && formik.errors.prize}
                                        required
                                    />

                                    <CustomInput
                                        label="Number of Winners"
                                        id="nWinners"
                                        name="nWinners"
                                        value={formik.values.nWinners}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        error={formik.touched.nWinners && formik.errors.nWinners}
                                        required
                                    />
                                    <CustomInput
                                        label="Rules"
                                        id="rules"
                                        name="rules"
                                        isTextArea={true}
                                        value={formik.values.rules}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        error={formik.touched.rules && formik.errors.rules}
                                        
                                    />
                                    {/* Conditionally render Question and Answer Fields */}
                                    {showQuestions && (
                                        <>
                                            <CustomInput
                                                label="Question"
                                                id="title"
                                                name="title"
                                                value={formik.values.title}
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                                error={formik.touched.title && formik.errors.title}
                                                required
                                            />

                                            {formik.values.answers.map((answer, index) => (
                                                <div key={index} className="d-flex align-items-center mb-3" style={{ position: "relative" }}>
                                                    <div className="flex-grow-1" style={{ position: "relative", zIndex: deletedAnswers.includes(index) ? 1 : 2 }}>
                                                        <CustomInput
                                                            label={`Answer ${index + 1}`}
                                                            id={`answers[${index}].title`}
                                                            name={`answers[${index}].title`}
                                                            value={answer.title}
                                                            onChange={(e) => handleAnswerChange(index, e.target.value)}
                                                            onBlur={formik.handleBlur}
                                                            error={
                                                                !deletedAnswers.includes(index) &&
                                                                formik.errors.answers &&
                                                                    formik.touched.answers &&
                                                                    typeof formik.errors.answers[index]?.title === "string"
                                                                    ? formik.errors.answers[index].title
                                                                    : null
                                                            }
                                                            required
                                                            disabled={deletedAnswers.includes(index)}
                                                        />
                                                    </div>
                                                    {formik.values.answers.length > 2 && !deletedAnswers.includes(index) && (
                                                        <button
                                                            type="button"
                                                            className="btn text-danger ms-2"
                                                            onClick={() => handleDeleteAnswer(index)}
                                                            title="Delete"
                                                            style={{ zIndex: 2 }}
                                                        >
                                                            Delete
                                                        </button>
                                                    )}
                                                    {deletedAnswers.includes(index) && (
                                                        <div
                                                            style={{
                                                                position: "absolute",
                                                                top: 0,
                                                                left: 0,
                                                                width: "100%",
                                                                height: "100%",
                                                                background: "rgba(0, 0, 0, 0.65)",
                                                                display: "flex",
                                                                justifyContent: "center",
                                                                alignItems: "center",
                                                                zIndex: 5,
                                                                borderRadius: "4px",
                                                            }}
                                                        >
                                                            <button
                                                                type="button"
                                                                onClick={() => handleUndoDeleteAnswer(index)}
                                                                className="simpleBtn"
                                                            >
                                                                <span>UNDO DELETE</span>
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                            ))}

                                            {formik.values.answers.length < 4 && (
                                                <div className="mb-4">
                                                    <button type="button" className="btn btn-outline-primary" onClick={addAnswer}>
                                                        Add Answer
                                                    </button>
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>
                            </>
                        )}

                        {step === 2 && (
                            <>
                                <h4 className="mb-2">Update Schedule Time</h4>
                                <p className="text-muted mb-3">
                                    You can schedule Contest to begin at your preferred time and date in the future.
                                </p>

                                <TimezoneSelect
                                    label="Timezone"
                                    id="tz"
                                    name="tz"
                                    value={formik.values.tz}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={formik.touched.tz && formik.errors.tz}
                                />

                                {selectedContest.attributes.isScheduled == 1 && (<div className="row">
                                    <div className="col-md-6">
                                        <CustomInput
                                            type="date"
                                            label="Start Date"
                                            name="startDate"
                                            value={formik.values.startDate}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            error={formik.touched.startDate && formik.errors.startDate}
                                        />
                                    </div>
                                    <div className="col-md-6">
                                        <CustomInput
                                            type="time"
                                            label="Start Time"
                                            name="startTime"
                                            value={formik.values.startTime}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            error={formik.touched.startTime && formik.errors.startTime}
                                        />
                                    </div>
                                </div>)}

                                <div className="row">
                                    <div className="col-md-6">
                                        <CustomInput
                                            type="date"
                                            label="End Date"
                                            name="endDate"
                                            value={formik.values.endDate}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            error={formik.touched.endDate && formik.errors.endDate}
                                        />
                                    </div>
                                    <div className="col-md-6">
                                        <CustomInput
                                            type="time"
                                            label="End Time"
                                            name="endTime"
                                            value={formik.values.endTime}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            error={formik.touched.endTime && formik.errors.endTime}
                                        />
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                    <div className="col-md-6 border-right p-3">
                        <h6 className="mb-3 text-decoration-underline text-center">Optional Information</h6>
                        <div className="col-md-12">
                            {/* Image Upload Section */}
                            <div className="d-flex flex-column align-items-center">
                                <div className="position-relative mb-3 w-100" style={{ position: "relative" }}>
                                    {croppedImage && !isImageDeleted ? (
                                        <div className="d-flex align-items-center">
                                            <img
                                                src={croppedImage}
                                                alt="Contest"
                                                className="img-fluid"
                                                style={{
                                                    width: "90%",
                                                    height: "120px",
                                                    objectFit: "contain",
                                                    border: "2px solid #ddd",
                                                    borderRadius: "8px",
                                                    backgroundColor: "#f8f9fa"
                                                }}
                                                onError={(e) => {
                                                    e.target.style.display = 'none';
                                                    // If image fails to load, show upload area
                                                    setCroppedImage(null);
                                                }}
                                            />
                                            <button
                                                type="button"
                                                onClick={handleDeleteImage}
                                                style={{ border: "0px", outline: "0px", cursor: "pointer", background: "none", marginLeft: "10px" }}
                                            >
                                                Delete
                                            </button>
                                        </div>
                                    ) : croppedImage && isImageDeleted ? (
                                        <div className="position-relative">
                                            <img
                                                src={croppedImage}
                                                alt="Contest"
                                                className="img-fluid"
                                                style={{
                                                    width: "100%",
                                                    height: "120px",
                                                    objectFit: "contain",
                                                    border: "2px solid #ddd",
                                                    borderRadius: "8px",
                                                    backgroundColor: "#f8f9fa"
                                                }}
                                            />
                                            <div
                                                style={{
                                                    position: "absolute",
                                                    top: 0,
                                                    left: 0,
                                                    width: "100%",
                                                    height: "100%",
                                                    background: "rgba(0, 0, 0, 0.65)",
                                                    display: "flex",
                                                    justifyContent: "center",
                                                    alignItems: "center",
                                                    zIndex: 5,
                                                    borderRadius: "8px",
                                                }}
                                            >
                                                <button
                                                    type="button"
                                                    onClick={handleUndoDeleteImage}
                                                    className="simpleBtn"
                                                >
                                                    <span>UNDO DELETE</span>
                                                </button>
                                            </div>
                                        </div>
                                    ) : (
                                        <div
                                            className="d-flex flex-column align-items-center justify-content-center position-relative"
                                            style={{
                                                width: "100%",
                                                height: "100px",
                                                backgroundColor: "#f8f9fa",
                                                border: "2px dashed #ddd",
                                                borderRadius: "8px",
                                                cursor: "pointer"
                                            }}
                                            onClick={() => document.getElementById('profileUpload').click()}
                                        >
                                            <FaImage size={40} color="#aaa" className="mb-2" />
                                            <small className="text-muted">
                                                {hasExistingImage ? "Click to change image" : "Click to upload image"}
                                            </small>
                                        </div>
                                    )}
                                    <label
                                        htmlFor="profileUpload"
                                        className="btn btn-sm btn-primary position-absolute"
                                        style={{
                                            bottom: "-15px",
                                            right: "50%",
                                            transform: "translateX(50%)",
                                            borderRadius: "50%",
                                            width: "30px",
                                            height: "30px",
                                            padding: "0",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
                                        }}
                                    >
                                        <FaPlus size={12} />
                                    </label>
                                    <input
                                        type="file"
                                        id="profileUpload"
                                        accept="image/*"
                                        onChange={handleFileChange}
                                        style={{ display: "none" }}
                                    />
                                </div>
                            </div>

                            {/* CKEditor Section */}
                            <div className="my-4">
                                <label htmlFor="poll-description" className="form-label">
                                    Additional Information <small className="text-muted">(Optional)</small>
                                </label>
                                <div className="ckeditor-container" style={{ minHeight: "100px" }}>
                                    <CKEditor
                                        editor={ClassicEditor}
                                        data={description}
                                        onChange={(event, editor) => {
                                            const data = editor.getData();
                                            setFormikDescription(data);
                                        }}
                                        config={{
                                            placeholder: "Add a detailed description for your contest...",
                                            toolbar: {
                                                items: [
                                                    'heading', '|',
                                                    'bold', 'italic', 'underline', 'strikethrough', '|',
                                                    'fontColor', 'fontBackgroundColor', '|',
                                                    'bulletedList', 'numberedList', '|',
                                                    'alignment', '|',
                                                    'link', '|',
                                                    'undo', 'redo'
                                                ]
                                            },
                                            language: 'en'
                                        }}
                                        onReady={(editor) => {
                                            editor.editing.view.change((writer) => {
                                                writer.setStyle(
                                                    'height',
                                                    '100px',
                                                    editor.editing.view.document.getRoot()
                                                );
                                            });
                                        }}
                                    />
                                </div>
                                {description && (
                                    <div className="mt-2">
                                        <small className="text-muted">
                                            Character count: {description.replace(/<[^>]*>/g, '').length}
                                        </small>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="col-md-12 ">
                        {step === 1 && (
                            <div className="d-flex justify-content-end mt-3 modalBtnContainer">
                                <button type="button" className="btn btn-outline-secondary me-2" onClick={handleClose}>
                                    Cancel
                                </button>
                                <button type="submit" className="btn btn-success"  
                                disabled={
                                    formik.isSubmitting
                                    }>
                                    {formik.isSubmitting ? "Updating..." : " Update Contest"}
                                </button>
                            </div>
                        )}
                        {step === 2 && (
                            <div className="d-flex justify-content-end mt-3 gap-2">
                                <button
                                    type="button"
                                    className="btn btn-outline-secondary"
                                    onClick={() => setStep(1)}
                                >
                                    Back
                                </button>
                                <button
                                    type="submit"
                                    className="btn btn-success"
                                    disabled={
                                        !formik.values.startDate &&
                                        !formik.values.startTime &&
                                        !formik.values.endDate &&
                                        !formik.values.endTime &&
                                        !formik.values.tz
                                    }
                                >
                                    {formik.isSubmitting ? "Updating..." : "Update Schedule"}
                                </button>
                            </div>
                        )}
                    </div>
                </form>
            </Modal>

            {/* Cropper Modal */}
            {showModal && (
                <CropperModalAdmin
                    show={showModal}
                    onClose={() => setShowModal(false)}
                    image={image}
                    onCrop={handleCrop}
                    aspectRatio={1242 / 180}
                />
            )}
        </>
    );
};

export default UpdateContest;